import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CalendarDays } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import useOrdersExport from '@/hooks/useOrdersExport';
import {
  EXPORT_DATE_PRESETS,
  ExportDatePreset,
  formatExportRange,
} from '@/utils/helpers/orderExport';
import { Field, PILL } from './ExportCustomFilters';

type OrdersExportState = ReturnType<typeof useOrdersExport>;

interface ExportPeriodProps {
  state: OrdersExportState;
  disabled: boolean;
}

/**
 * Owner 10-05 — the export Period, shared by This tab, All orders and Custom
 * (not Selected): preset pills or a calendar range, default Last 30 days. Core
 * defaults to 30 days too, so a big merchant never dumps all of history.
 *
 * Owner 10-06 — the calendar is INLINE, under the pills: a popover flipped up
 * and down with the space left, and a click beside it closed the whole modal.
 * One click picks "from that day to today"; a second click moves the end.
 */
export function ExportPeriod({ state, disabled }: ExportPeriodProps) {
  const { t, i18n } = useTranslation();
  const [pickingEnd, setPickingEnd] = useState(false);
  const { from, to } = state;
  // Controlled month: the view stays where the merchant paged to, whatever re-renders.
  const [month, setMonth] = useState(() => (to ?? dayjs()).toDate());
  const label = String(t('export_modal.period'));
  const showCalendar = state.preset === 'custom';

  const onPreset = (next: string) => {
    if (!next) return;
    state.changePreset(next as ExportDatePreset);
    setPickingEnd(false);
    setMonth(new Date());
  };

  // 1st click: that day → today. 2nd click: the end (swapped if earlier).
  const onDayClick = (day: Date) => {
    const picked = dayjs(day).startOf('day');
    if (pickingEnd && from) {
      const [start, end] = picked.isBefore(from, 'day') ? [picked, from] : [from, picked];
      state.changeRange(start, end);
      setPickingEnd(false);
      return;
    }
    state.changeRange(picked, dayjs().startOf('day'));
    setPickingEnd(true);
  };

  return (
    <Field label={label}>
      <ToggleGroup
        type='single'
        value={state.preset}
        onValueChange={onPreset}
        disabled={disabled}
        aria-label={label}
        className='flex-wrap justify-start gap-2'
      >
        {EXPORT_DATE_PRESETS.map((preset) => (
          <ToggleGroupItem key={preset} value={preset} className={PILL}>
            {String(t(`export_modal.preset_${preset}`))}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {showCalendar ? (
        <div className='w-fit rounded-2xl border border-solid border-sd-border bg-white'>
          <div className='flex items-center gap-2 border-0 border-b border-solid border-sd-border px-3 py-2 text-sm font-medium text-sd-foreground'>
            <CalendarDays className='h-4 w-4 text-sd-muted-foreground' aria-hidden />
            <bdi aria-label={String(t('export_modal.date_range'))}>
              {from && to
                ? formatExportRange(from, to, i18n.language)
                : String(t('export_modal.pick_date'))}
            </bdi>
          </div>
          <Calendar
            mode='range'
            fixedWeeks
            selected={{ from: from?.toDate(), to: to?.toDate() }}
            month={month}
            onMonthChange={setMonth}
            disabled={disabled ? true : { after: new Date() }}
            onDayClick={onDayClick}
          />
          <p className='m-0 px-3 pb-3 text-xs text-sd-muted-foreground'>
            {String(t(pickingEnd ? 'export_modal.pick_end_hint' : 'export_modal.pick_start_hint'))}
          </p>
        </div>
      ) : null}
      {state.dateError ? (
        <p role='alert' className='m-0 text-xs text-red-600'>
          {String(t(state.dateError))}
        </p>
      ) : null}
    </Field>
  );
}
